// Rate limits: who the request limiter is holding back (login counters and
// progressive bans), how long each has left, and clearing them for someone
// locked out. Over /api/admin/v2/rate-limit.
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Ban,
  CheckCircle2,
  Gauge,
  Lock,
  RefreshCw,
  Search,
  ShieldAlert,
  Unlock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import useDebounce from '@/client/hooks/useDebounce';
import {
  useRateLimitAccountClearMutation,
  useRateLimitAccountQuery,
  useRateLimitAuditQuery,
  useRateLimitBansQuery,
  useRateLimitClearMutation,
  useRateLimitEventsQuery,
  useRateLimitLimitsQuery,
  useRateLimitLookupQuery,
  useRateLimitOverviewQuery,
  useRateLimitRefresh,
} from '@/client/api/rateLimit/rateLimit.query';
import {
  AccountClearPart,
  AccountLookupResponse,
  BanRow,
  BanStateFilter,
  ClearPart,
  ClientRef,
  LimitCounter,
  LimitScope,
  LimitState,
  LimiterEvent,
  StoreStatus,
} from '@/client/types/rateLimit';
import { getApiErrorCode, getApiErrorField, getApiErrorMessage } from '@/client/utils/errors';
import {
  Card,
  EmptyRow,
  Field,
  FormDialog,
  INPUT_CLASS,
  LoadingNote,
  PageShell,
  StatCard,
  Tabs,
  Th,
  fmtAgo,
  fmtNum,
  fmtTime,
} from '../Cloud/cloudUi';

export const RATE_LIMIT_BASE = '/admin/rate-limits';

type Tab = 'overview' | 'user' | 'lookup' | 'limits' | 'bans' | 'events' | 'audit';
const TABS: Tab[] = ['overview', 'user', 'lookup', 'limits', 'bans', 'events', 'audit'];
const TAB_LABEL: Record<Tab, string> = {
  overview: 'Overview',
  user: 'Find a user',
  lookup: 'IP lookup',
  limits: 'Limits',
  bans: 'Bans',
  events: 'Events',
  audit: 'Audit',
};

const tabPath = (t: Tab) => (t === 'overview' ? RATE_LIMIT_BASE : `${RATE_LIMIT_BASE}/${t}`);
const lookupPath = (c: Pick<ClientRef, 'client' | 'client_id' | 'client_kind'>) =>
  `${RATE_LIMIT_BASE}/lookup?${
    c.client_kind === 'token'
      ? `client_id=${encodeURIComponent(c.client_id)}`
      : `client=${encodeURIComponent(c.client)}`
  }`;

/* ---------- permissions ---------- */

const useRateLimitPerms = () => {
  const view = useHasPermission('ratelimit.view');
  const manage = useHasPermission('ratelimit.manage');
  return {
    loading: view.isLoading,
    canView: view.isSuperAdmin || view.hasPermission,
    canManage: manage.isSuperAdmin || manage.hasPermission,
  };
};
type Perms = ReturnType<typeof useRateLimitPerms>;

/* ---------- time ---------- */

export const fmtSecs = (s?: number | null) => {
  if (s == null || s <= 0) return '—';
  const sec = Math.ceil(s);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  const h = Math.floor(sec / 3600);
  return `${h}h ${Math.floor((sec % 3600) / 60)}m`;
};

/** Ticks once a second so a wait counts down between refetches. */
const useNow = () => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
};

/** Seconds left, as of when the server answered (`at`, ms), counted down live. */
const Countdown: React.FC<{ seconds: number; at: number; className?: string }> = ({
  seconds,
  at,
  className,
}) => {
  const now = useNow();
  const left = seconds - (now - at) / 1000;
  return (
    <span className={className ?? 'font-medium tabular-nums text-gray-900'}>
      {left > 0 ? fmtSecs(left) : 'now'}
    </span>
  );
};

/* ---------- small pieces ---------- */

const ERROR_COPY: Record<string, string> = {
  missing_reason: 'A reason is required.',
  missing_client: 'Enter an IP address or client key.',
  client_not_found: 'Nothing is held against that client.',
  store_not_shared:
    'Limit counters are kept per worker (RATE_LIMIT_STORE is not redis), so they cannot be cleared here. They expire on their own; bans can still be cleared.',
  redis_unavailable: 'The ban store is not reachable.',
  invalid_what: 'Choose what to clear.',
};

const errorText = (error: unknown) => {
  const code = getApiErrorCode(error);
  const detail = getApiErrorField<string>(error, 'detail');
  return (code && ERROR_COPY[code]) || [getApiErrorMessage(error, 'Something went wrong'), detail].filter(Boolean).join(' ');
};

const ErrorNotice: React.FC<{ error: unknown; onRetry?: () => void }> = ({ error, onRetry }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
    <span>{errorText(error)}</span>
    {onRetry && (
      <Button size="sm" variant="outline" onClick={onRetry}>
        <RefreshCw className="mr-2 h-3.5 w-3.5" />
        Retry
      </Button>
    )}
  </div>
);

const Frame: React.FC<{
  q: { isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  loading?: React.ReactNode;
  children: React.ReactNode;
}> = ({ q, loading, children }) => (
  <>
    {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
    {q.isLoading ? (
      <>
        <LoadingNote>{loading ?? 'Reading the limiter state.'}</LoadingNote>
        <Skeleton className="h-48 w-full" />
      </>
    ) : (
      children
    )}
  </>
);

const Notice: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">{children}</div>
);

/** Per-worker counters are invisible from here; say so instead of showing an empty table. */
const StoreNotice: React.FC<{ s?: StoreStatus }> = ({ s }) =>
  !s ? null : !s.store_shared ? (
    <div className="rounded-xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-900">
      <p className="font-medium">Limit counters are kept per worker</p>
      <p className="mt-1 text-amber-800">
        RATE_LIMIT_STORE is <span className="font-mono">{s.store}</span>, so each API worker counts on its
        own and nothing outside it can read or clear the counters. They clear themselves (login: within a
        minute). Bans are shared and are shown below. Set RATE_LIMIT_STORE=redis to manage counters here.
      </p>
    </div>
  ) : !s.store_reachable ? (
    <Notice>The limiter&apos;s Redis store is not reachable from the admin API.</Notice>
  ) : null;

const TruncatedNote: React.FC<{ truncated?: boolean }> = ({ truncated }) =>
  truncated ? (
    <p className="text-xs text-amber-700">
      The keyspace is large; only the first 50,000 keys were read, so these figures are a lower bound.
    </p>
  ) : null;

const ClientCell: React.FC<{ c: ClientRef }> = ({ c }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(lookupPath(c))}
      className="text-left font-mono text-xs text-blue-700 hover:underline"
      title={c.client_kind === 'token' ? 'Signed-in caller, keyed by session token (masked)' : undefined}
    >
      {c.client}
      {c.client_kind !== 'ip' && (
        <span className="ml-1.5 rounded bg-gray-100 px-1 py-0.5 font-sans text-[10px] text-gray-600">
          {c.client_kind}
        </span>
      )}
    </button>
  );
};

const endpointLabel = (e: string) => e.split('.').pop()?.replace(/^api_v\d_/, '') ?? e;
const limitLabel = (l: LimitCounter['limit']) =>
  `${l.count} / ${l.period_seconds >= 3600 ? `${l.period_seconds / 3600}h` : l.period_seconds >= 60 ? `${l.period_seconds / 60}m` : `${l.period_seconds}s`}`;

const StatusPill: React.FC<{ blocked: boolean; label?: string }> = ({ blocked, label }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
      blocked ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
    }`}
  >
    {blocked ? <Lock className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
    {label ?? (blocked ? 'blocked' : 'allowed')}
  </span>
);

/* ---------- clearing ---------- */

const PART_COPY: Record<ClearPart, { label: string; hint: string }> = {
  limits: { label: 'Limit counters', hint: 'Restores the full budget on every endpoint (e.g. 5 login attempts).' },
  ban: { label: 'Active ban', hint: 'Lifts the ban now. Also clears strikes, so the next 429 does not re-ban straight away.' },
  strikes: { label: 'Strikes', hint: 'Recent 429s counting towards a ban.' },
  level: { label: 'Escalation level', hint: 'The next ban starts again at the shortest duration.' },
};

type ClearTarget = ClientRef & { endpoint?: string; defaults?: ClearPart[] };

const ClearDialog: React.FC<{ target: ClearTarget; storeShared: boolean; onClose: () => void }> = ({
  target,
  storeShared,
  onClose,
}) => {
  const mut = useRateLimitClearMutation();
  const [parts, setParts] = useState<ClearPart[]>(
    (target.defaults ?? ['limits', 'ban']).filter((p) => storeShared || p !== 'limits'),
  );
  const toggle = (p: ClearPart) =>
    setParts((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  return (
    <FormDialog
      title={`Clear rate limiting for ${target.client}`}
      description={
        target.endpoint
          ? `Only the ${endpointLabel(target.endpoint)} counter will be reset.`
          : 'They can try again immediately. Recorded in the audit log with your reason.'
      }
      confirmLabel="Clear"
      destructive
      valid={parts.length > 0}
      busy={mut.isPending}
      error={mut.error}
      onClose={onClose}
      onConfirm={(reason) =>
        mut.mutate(
          {
            ...(target.client_kind === 'token' ? { client_id: target.client_id } : { client: target.client }),
            what: parts,
            endpoint: target.endpoint,
            reason,
          },
          {
            onSuccess: (r) => {
              const c = r.cleared;
              const bits = [
                c.limits ? `${c.limits} counter${c.limits === 1 ? '' : 's'}` : null,
                c.ban ? 'the ban' : null,
                c.strikes ? 'strikes' : null,
                c.level ? 'escalation level' : null,
              ].filter(Boolean);
              toast.success(
                bits.length ? `Cleared ${bits.join(', ')} for ${r.client}.` : 'Nothing was held against this client.',
              );
              onClose();
            },
          },
        )
      }
    >
      {!target.endpoint && (
        <div className="space-y-2">
          {(Object.keys(PART_COPY) as ClearPart[]).map((p) => {
            const disabled = p === 'limits' && !storeShared;
            return (
              <label key={p} className={`flex items-start gap-2 text-sm ${disabled ? 'opacity-50' : ''}`}>
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={parts.includes(p)}
                  disabled={disabled}
                  onChange={() => toggle(p)}
                />
                <span>
                  <span className="font-medium text-gray-800">{PART_COPY[p].label}</span>
                  <span className="block text-xs text-gray-500">
                    {disabled ? 'Per-worker store: cannot be cleared from here.' : PART_COPY[p].hint}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      )}
    </FormDialog>
  );
};

/* ---------- tables ---------- */

const LimitsTable: React.FC<{
  rows: LimitCounter[];
  at: number;
  empty: string;
  perms: Perms;
  onClear: (t: ClearTarget) => void;
  showClient?: boolean;
}> = ({ rows, at, empty, perms, onClear, showClient = true }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-left text-xs text-gray-500">
        <tr>
          {showClient && <Th>Client</Th>}
          <Th>Endpoint</Th>
          <Th>Limit</Th>
          <Th>Used</Th>
          <Th>Status</Th>
          <Th>Can retry in</Th>
          <Th>Fully resets in</Th>
          {perms.canManage && <Th right />}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.length === 0 ? (
          <EmptyRow cols={showClient ? 8 : 7}>{empty}</EmptyRow>
        ) : (
          rows.map((r) => (
            <tr key={r.id} className={r.blocked ? 'bg-red-50/40' : ''}>
              {showClient && (
                <td className="px-4 py-2">
                  <ClientCell c={r} />
                </td>
              )}
              <td className="px-4 py-2">
                <span className="text-gray-900" title={r.endpoint}>
                  {endpointLabel(r.endpoint)}
                </span>
                {r.app !== 'app' && <span className="ml-1 text-xs text-gray-400">({r.app})</span>}
              </td>
              <td className="px-4 py-2 text-gray-600">{limitLabel(r.limit)}</td>
              <td className="px-4 py-2 tabular-nums text-gray-700">
                {r.used} <span className="text-gray-400">of {r.limit.count}</span>
              </td>
              <td className="px-4 py-2">
                <StatusPill blocked={r.blocked} />
              </td>
              <td className="px-4 py-2">
                {r.blocked ? <Countdown seconds={r.retry_after_seconds} at={at} /> : <span className="text-gray-400">now</span>}
              </td>
              <td className="px-4 py-2 text-gray-600">
                <Countdown seconds={r.reset_in_seconds} at={at} className="tabular-nums" />
              </td>
              {perms.canManage && (
                <td className="px-4 py-2 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onClear({ ...r, endpoint: r.endpoint, defaults: ['limits'] })}
                  >
                    Reset
                  </Button>
                </td>
              )}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const BansTable: React.FC<{
  rows: BanRow[];
  at: number;
  empty: string;
  perms: Perms;
  onClear: (t: ClearTarget) => void;
}> = ({ rows, at, empty, perms, onClear }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-left text-xs text-gray-500">
        <tr>
          <Th>Client</Th>
          <Th>Status</Th>
          <Th>Ban ends in</Th>
          <Th>Level</Th>
          <Th>Strikes</Th>
          <Th>Next ban would last</Th>
          {perms.canManage && <Th right />}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.length === 0 ? (
          <EmptyRow cols={7}>{empty}</EmptyRow>
        ) : (
          rows.map((b) => (
            <tr key={b.client_id} className={b.banned ? 'bg-red-50/40' : ''}>
              <td className="px-4 py-2">
                <ClientCell c={b} />
              </td>
              <td className="px-4 py-2">
                <StatusPill blocked={b.banned} label={b.banned ? 'banned' : 'not banned'} />
              </td>
              <td className="px-4 py-2">
                {b.banned ? <Countdown seconds={b.ban_remaining_seconds} at={at} /> : <span className="text-gray-400">—</span>}
              </td>
              <td className="px-4 py-2 text-gray-700">
                {b.ban_level ?? b.escalation_level ?? 0}
                {b.escalation_level > 0 && (
                  <span className="ml-1 text-xs text-gray-400">
                    (resets in <Countdown seconds={b.escalation_reset_in_seconds} at={at} className="" />)
                  </span>
                )}
              </td>
              <td className="px-4 py-2 text-gray-700">
                {b.strikes}
                {b.strikes > 0 && <span className="ml-1 text-xs text-gray-400">({b.strikes_to_ban} to ban)</span>}
              </td>
              <td className="px-4 py-2 text-gray-600">{fmtSecs(b.next_ban_seconds)}</td>
              {perms.canManage && (
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => onClear({ ...b, defaults: ['ban', 'limits'] })}>
                    <Unlock className="mr-1.5 h-3.5 w-3.5" />
                    Unblock
                  </Button>
                </td>
              )}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const EventsTable: React.FC<{ rows: LimiterEvent[]; empty: string; showClient?: boolean }> = ({
  rows,
  empty,
  showClient = true,
}) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-left text-xs text-gray-500">
        <tr>
          <Th>When</Th>
          <Th>Event</Th>
          {showClient && <Th>Client</Th>}
          <Th>Request</Th>
          <Th>User agent</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.length === 0 ? (
          <EmptyRow cols={showClient ? 5 : 4}>{empty}</EmptyRow>
        ) : (
          rows.map((e, i) => (
            <tr key={`${e.timestamp}-${i}`}>
              <td className="whitespace-nowrap px-4 py-2 text-gray-600" title={fmtTime(e.timestamp)}>
                {fmtAgo(e.timestamp)}
              </td>
              <td className="px-4 py-2">
                {e.event === 'ban_rejected' ? (
                  <span className="text-red-700">refused (banned, level {e.ban_level})</span>
                ) : e.banned ? (
                  <span className="font-medium text-red-700">429 → banned {fmtSecs(e.ban_duration_seconds)}</span>
                ) : (
                  <span className="text-amber-700">429 rate limited</span>
                )}
              </td>
              {showClient && (
                <td className="px-4 py-2">
                  {e.client_id ? <ClientCell c={e as ClientRef} /> : <span className="text-gray-400">—</span>}
                  {e.remote_ip && e.remote_ip !== e.client && (
                    <span className="block text-xs text-gray-400">edge {e.remote_ip}</span>
                  )}
                </td>
              )}
              <td className="px-4 py-2 font-mono text-xs text-gray-700">
                {e.method} {e.path}
              </td>
              <td className="max-w-xs truncate px-4 py-2 text-xs text-gray-500" title={e.user_agent}>
                {e.user_agent || '—'}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

/* ---------- tabs ---------- */

type TabProps = { perms: Perms; onClear: (t: ClearTarget) => void };

const OverviewTab: React.FC<TabProps> = ({ perms, onClear }) => {
  const q = useRateLimitOverviewQuery();
  const bans = useRateLimitBansQuery({ state: 'banned' });
  const blocked = useRateLimitLimitsQuery({ scope: 'auth', state: 'blocked' });
  const d = q.data;
  const ev = d?.events_24h;
  return (
    <Frame q={q}>
      {d && (
        <div className="space-y-5">
          <Card title="Someone can't sign in?">
            <AccountSearch />
          </Card>
          <StoreNotice s={d} />
          <TruncatedNote truncated={d.truncated} />
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="Locked out of login"
              value={fmtNum(d.counts.blocked_auth_clients)}
              hint={d.store_shared ? 'clients over a sign-in limit now' : 'not visible: per-worker store'}
              icon={<Lock className="h-5 w-5 text-red-600" />}
              color="bg-red-50"
            />
            <StatCard
              label="Banned"
              value={fmtNum(d.counts.banned_clients)}
              hint="refused on every path"
              icon={<Ban className="h-5 w-5 text-rose-600" />}
              color="bg-rose-50"
            />
            <StatCard
              label="With strikes"
              value={fmtNum(d.counts.clients_with_strikes)}
              hint={`${d.config.ban_strike_threshold} strikes in ${fmtSecs(d.config.ban_strike_window_seconds)} = ban`}
              icon={<ShieldAlert className="h-5 w-5 text-amber-600" />}
              color="bg-amber-50"
            />
            <StatCard
              label="429s, last 24h"
              value={ev?.error ? '—' : fmtNum(ev?.rate_limited ?? 0)}
              hint={ev?.error ? 'limiter log unavailable' : `${fmtNum(ev?.ban_rejected ?? 0)} refused by a ban`}
              icon={<Gauge className="h-5 w-5 text-blue-600" />}
              color="bg-blue-50"
            />
          </div>

          <Card title="Locked out of sign-in right now" note={`${blocked.data?.total ?? 0} counters`} flush>
            <LimitsTable
              rows={blocked.data?.limits ?? []}
              at={blocked.dataUpdatedAt}
              empty={d.store_shared ? 'Nobody is over a sign-in limit.' : 'Counters are per worker and cannot be read.'}
              perms={perms}
              onClear={onClear}
            />
          </Card>

          <Card title="Active bans" note={`${bans.data?.total ?? 0} clients`} flush>
            <BansTable
              rows={bans.data?.bans ?? []}
              at={bans.dataUpdatedAt}
              empty="Nobody is banned."
              perms={perms}
              onClear={onClear}
            />
          </Card>

          <div className="grid gap-5 xl:grid-cols-2">
            <Card title="Endpoints under pressure" note="active counters" flush>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs text-gray-500">
                    <tr>
                      <Th>Endpoint</Th>
                      <Th>Limit</Th>
                      <Th right>Clients</Th>
                      <Th right>Blocked</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {d.endpoints.length === 0 ? (
                      <EmptyRow cols={4}>{d.store_shared ? 'No endpoint has spent budget.' : 'Not visible: per-worker store.'}</EmptyRow>
                    ) : (
                      d.endpoints.map((e) => (
                        <tr key={`${e.app}-${e.endpoint}-${e.limit.count}`}>
                          <td className="px-4 py-2 text-gray-900" title={e.endpoint}>
                            {endpointLabel(e.endpoint)}
                          </td>
                          <td className="px-4 py-2 text-gray-600">{limitLabel(e.limit)}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{fmtNum(e.clients)}</td>
                          <td className={`px-4 py-2 text-right tabular-nums ${e.blocked ? 'font-medium text-red-700' : 'text-gray-400'}`}>
                            {fmtNum(e.blocked)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
            <Card title="Most limited, last 24h" flush>
              <div className="grid divide-y divide-gray-100 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
                <div className="p-4">
                  <p className="mb-2 text-xs font-medium text-gray-500">Paths</p>
                  {(ev?.top_paths ?? []).length === 0 && <p className="text-sm text-gray-400">None.</p>}
                  {(ev?.top_paths ?? []).map((p) => (
                    <div key={p.path} className="flex justify-between gap-2 py-0.5 text-sm">
                      <span className="truncate font-mono text-xs text-gray-700" title={p.path}>{p.path}</span>
                      <span className="tabular-nums text-gray-900">{fmtNum(p.count)}</span>
                    </div>
                  ))}
                </div>
                <div className="p-4">
                  <p className="mb-2 text-xs font-medium text-gray-500">Clients</p>
                  {(ev?.top_clients ?? []).length === 0 && <p className="text-sm text-gray-400">None.</p>}
                  {(ev?.top_clients ?? []).map((c) => (
                    <div key={c.client_id} className="flex justify-between gap-2 py-0.5 text-sm">
                      <ClientCell c={c} />
                      <span className="tabular-nums text-gray-900">{fmtNum(c.count)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>

          <Card title="How the limiter is configured" note={`as of ${new Date(d.generated_at).toLocaleTimeString()}`}>
            <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-6">
              <Field label="Counter store">
                {d.store}
                {d.store_shared && !d.store_reachable ? ' (unreachable)' : ''}
              </Field>
              <Field label="Login limit">{d.config.login_limit}, per IP</Field>
              <Field label="Default limit">
                {d.config.default_limit_per_minute ? `${d.config.default_limit_per_minute} / min` : 'off'}
              </Field>
              <Field label="Ban after">
                {d.config.ban_strike_threshold} × 429 within {fmtSecs(d.config.ban_strike_window_seconds)}
              </Field>
              <Field label="Ban durations">{d.config.ban_durations_seconds.map(fmtSecs).join(' → ')}</Field>
              <Field label="Escalation resets after">{fmtSecs(d.config.ban_level_window_seconds)}</Field>
            </div>
          </Card>
        </div>
      )}
    </Frame>
  );
};

const LookupTab: React.FC<TabProps> = ({ perms, onClear }) => {
  const [params, setParams] = useSearchParams();
  const client = params.get('client') ?? '';
  const clientId = params.get('client_id') ?? '';
  const [input, setInput] = useState(client);
  useEffect(() => setInput(client), [client]);
  const q = useRateLimitLookupQuery({ client, client_id: clientId });
  const d = q.data;
  const at = q.dataUpdatedAt;

  return (
    <div className="space-y-5">
      <Card title="Look up a client">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const v = input.trim();
            setParams(v ? { client: v } : {});
          }}
        >
          <label className="block min-w-[18rem] flex-1 text-sm">
            <span className="text-gray-700">IP address or client key</span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. 102.176.4.21"
              className={`${INPUT_CLASS} mt-1 w-full font-mono`}
            />
          </label>
          <Button type="submit" size="sm">
            <Search className="mr-2 h-3.5 w-3.5" />
            Look up
          </Button>
        </form>
        <p className="mt-2 text-xs text-gray-500">
          For when you already have an IP, e.g. from the Events or Bans tab. To help a person, use Find a user
          with their email or phone.
        </p>
      </Card>

      {(client || clientId) && (
        <Frame q={q} loading="Reading this client's state.">
          {d && (
            <>
              <StoreNotice s={d} />
              <div
                className={`flex flex-wrap items-center justify-between gap-4 rounded-xl border p-5 shadow-sm ${
                  d.can_login ? 'border-green-100 bg-green-50' : 'border-red-100 bg-red-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  {d.can_login ? (
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  ) : (
                    <Lock className="h-8 w-8 text-red-600" />
                  )}
                  <div>
                    <p className="font-mono text-sm text-gray-700">{d.client}</p>
                    {d.can_login ? (
                      <p className="text-lg font-semibold text-green-800">Can sign in now</p>
                    ) : (
                      <p className="text-lg font-semibold text-red-800">
                        Can sign in again in{' '}
                        <Countdown seconds={d.login_wait_seconds} at={at} className="tabular-nums" />
                      </p>
                    )}
                    <p className="text-xs text-gray-600">
                      {d.ban.banned
                        ? `Banned at level ${d.ban.ban_level}: every request is refused, not only sign-in.`
                        : d.can_login
                          ? 'Nothing is blocking sign-in from this client.'
                          : 'Over the sign-in attempt limit.'}
                    </p>
                  </div>
                </div>
                {perms.canManage && (
                  <Button variant="destructive" onClick={() => onClear({ ...d, defaults: ['limits', 'ban'] })}>
                    <Unlock className="mr-2 h-4 w-4" />
                    Clear and let them in
                  </Button>
                )}
              </div>

              <Card title="Ban" flush>
                <BansTable
                  rows={d.ban.banned || d.ban.strikes || d.ban.escalation_level ? [{ ...d, ...d.ban }] : []}
                  at={at}
                  empty="No ban, strikes or escalation."
                  perms={perms}
                  onClear={onClear}
                />
              </Card>
              <Card title="Limit counters" note="endpoints with budget spent" flush>
                <LimitsTable
                  rows={d.limits}
                  at={at}
                  showClient={false}
                  empty={d.store_shared ? 'No budget spent on any endpoint.' : 'Counters are per worker and cannot be read.'}
                  perms={perms}
                  onClear={onClear}
                />
              </Card>
              <Card title="Recent 429s and refusals" note="last 24h" flush>
                <EventsTable rows={d.events} showClient={false} empty="No limiter events for this client." />
              </Card>
            </>
          )}
        </Frame>
      )}
    </div>
  );
};

// Each search runs a Redis SCAN or a log query, so wait for typing to pause.
const SEARCH_DEBOUNCE_MS = 400;

const SearchBox: React.FC<{ value: string; onChange: (v: string) => void; placeholder: string }> = ({
  value,
  onChange,
  placeholder,
}) => (
  <input
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className={`${INPUT_CLASS} w-56`}
  />
);

const LimitsTab: React.FC<TabProps> = ({ perms, onClear }) => {
  const [scope, setScope] = useState<LimitScope>('auth');
  const [state, setState] = useState<LimitState>('blocked');
  const [search, setSearch] = useState('');
  const term = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);
  const q = useRateLimitLimitsQuery({ scope, state, q: term });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={scope} onChange={setScope} options={['auth', 'all'] as const} />
        <Tabs value={state} onChange={setState} options={['blocked', 'active', 'all'] as const} />
        <SearchBox value={search} onChange={setSearch} placeholder="IP, key or endpoint" />
      </div>
      <Frame q={q}>
        <StoreNotice s={q.data} />
        <TruncatedNote truncated={q.data?.truncated} />
        <Card
          title={scope === 'auth' ? 'Sign-in limits' : 'All limits'}
          note={`${q.data?.total ?? 0} counters${(q.data?.total ?? 0) > (q.data?.limits.length ?? 0) ? `, first ${q.data?.limits.length}` : ''}`}
          flush
        >
          <LimitsTable
            rows={q.data?.limits ?? []}
            at={q.dataUpdatedAt}
            empty={state === 'blocked' ? 'Nobody is over a limit.' : 'No budget spent.'}
            perms={perms}
            onClear={onClear}
          />
        </Card>
      </Frame>
    </div>
  );
};

const BansTab: React.FC<TabProps> = ({ perms, onClear }) => {
  const [state, setState] = useState<BanStateFilter>('banned');
  const [search, setSearch] = useState('');
  const term = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);
  const q = useRateLimitBansQuery({ state, q: term });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={state} onChange={setState} options={['banned', 'all'] as const} />
        <SearchBox value={search} onChange={setSearch} placeholder="IP or key" />
      </div>
      <Frame q={q}>
        {q.data && !q.data.redis_available && (
          <Notice>The ban store (Redis) is not reachable from the admin API.</Notice>
        )}
        <TruncatedNote truncated={q.data?.truncated} />
        <Card
          title={state === 'banned' ? 'Banned now' : 'Bans, strikes and escalation'}
          note={`${q.data?.total ?? 0} clients`}
          flush
        >
          <BansTable
            rows={q.data?.bans ?? []}
            at={q.dataUpdatedAt}
            empty={state === 'banned' ? 'Nobody is banned.' : 'No client has strikes or a ban.'}
            perms={perms}
            onClear={onClear}
          />
        </Card>
      </Frame>
    </div>
  );
};

const SINCE: Record<string, number> = { '1h': 3600, '24h': 86400, '7d': 7 * 86400 };

const EventsTab: React.FC<TabProps> = () => {
  const [event, setEvent] = useState<'all' | '429' | 'ban'>('all');
  const [since, setSince] = useState<keyof typeof SINCE>('24h');
  const [search, setSearch] = useState('');
  const term = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);
  const q = useRateLimitEventsQuery({
    event: event === '429' ? 'rate_limit_exceeded' : event === 'ban' ? 'ban_rejected' : '',
    q: term,
    since: SINCE[since],
  });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={event} onChange={setEvent} options={['all', '429', 'ban'] as const} />
        <Tabs value={since} onChange={setSince} options={Object.keys(SINCE) as (keyof typeof SINCE)[]} />
        <SearchBox value={search} onChange={setSearch} placeholder="path, IP or user agent" />
      </div>
      <Frame q={q}>
        {q.data?.events_error && <Notice>The limiter log (ff_log.limiter) is not readable.</Notice>}
        <Card title="Limiter events" note="every 429, and every request a ban refused" flush>
          <EventsTable rows={q.data?.events ?? []} empty="No events in this window." />
        </Card>
      </Frame>
    </div>
  );
};

const AuditTab: React.FC<TabProps> = () => {
  const q = useRateLimitAuditQuery();
  const rows = q.data?.audit ?? [];
  return (
    <Frame q={q} loading="Reading what the console cleared.">
      <Card title="What the console cleared" flush>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <Th>When</Th>
                <Th>By</Th>
                <Th>Client</Th>
                <Th>Cleared</Th>
                <Th>Reason</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 ? (
                <EmptyRow cols={5}>Nothing has been cleared from the console yet.</EmptyRow>
              ) : (
                rows.map((r, i) => (
                  <tr key={`${r.at}-${i}`}>
                    <td className="whitespace-nowrap px-4 py-2 text-gray-600" title={fmtTime(r.at)}>
                      {fmtAgo(r.at)}
                      {r.env && <span className="ml-1 text-xs text-gray-400">{r.env}</span>}
                    </td>
                    <td className="px-4 py-2 text-gray-700">{r.by}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-700">{r.client}</td>
                    <td className="px-4 py-2 text-gray-700">
                      {(r.what ?? []).join(', ')}
                      {r.endpoint && <span className="block text-xs text-gray-400">{endpointLabel(r.endpoint)}</span>}
                    </td>
                    <td className="max-w-sm px-4 py-2 text-gray-600">{r.reason}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </Frame>
  );
};

/* ---------- one person ---------- */

const userPath = (q: string) => `${RATE_LIMIT_BASE}/user?q=${encodeURIComponent(q)}`;

/** Email / username / phone box. Used on the Find a user tab and the overview. */
const AccountSearch: React.FC<{ initial?: string; autoFocus?: boolean }> = ({ initial = '', autoFocus }) => {
  const navigate = useNavigate();
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (value.trim()) navigate(userPath(value.trim()));
      }}
    >
      <label className="block min-w-[18rem] flex-1 text-sm">
        <span className="text-gray-700">The person&apos;s email, username or phone number</span>
        <input
          value={value}
          autoFocus={autoFocus}
          onChange={(e) => setValue(e.target.value)}
          placeholder="e.g. ama@example.com"
          className={`${INPUT_CLASS} mt-1 w-full`}
        />
      </label>
      <Button type="submit" size="sm">
        <Search className="mr-2 h-3.5 w-3.5" />
        Check
      </Button>
    </form>
  );
};

const AccountClearDialog: React.FC<{ d: AccountLookupResponse; onClose: () => void }> = ({ d, onClose }) => {
  const mut = useRateLimitAccountClearMutation();
  const anyHold = d.holds.some((h) => h.held || h.failures > 0);
  const [hold, setHold] = useState(anyHold);
  const [keycloak, setKeycloak] = useState(!!d.keycloak.locked);
  // A shared IP is never ticked for you: clearing it lets every account on it in.
  const blocking = d.ips.filter((ip) => ip.banned || ip.login_blocked);
  const [ips, setIps] = useState<string[]>(blocking.filter((ip) => !ip.shared).map((ip) => ip.client));
  const toggleIp = (c: string) => setIps((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));
  const what: AccountClearPart[] = [
    ...(hold ? (['hold'] as const) : []),
    ...(keycloak ? (['keycloak'] as const) : []),
    ...(ips.length ? (['ips'] as const) : []),
  ];

  return (
    <FormDialog
      title={`Let ${d.account?.name || d.account?.username || d.query} sign in`}
      description="They can try again straight away. Recorded in the audit log with your reason."
      confirmLabel="Clear"
      destructive
      valid={what.length > 0}
      busy={mut.isPending}
      error={mut.error}
      onClose={onClose}
      onConfirm={(reason) =>
        mut.mutate(
          { account: d.query, what, ips, reason },
          {
            onSuccess: (r) => {
              const c = r.cleared;
              const bits = [
                c.holds ? 'the wrong-password hold' : null,
                c.keycloak ? 'the Keycloak lock' : null,
                c.ips.length ? `${c.ips.length} IP${c.ips.length === 1 ? '' : 's'}` : null,
              ].filter(Boolean);
              toast.success(bits.length ? `Cleared ${bits.join(', ')}.` : 'Nothing was holding this person back.');
              onClose();
            },
          },
        )
      }
    >
      <div className="space-y-2 text-sm">
        <label className="flex items-start gap-2">
          <input type="checkbox" className="mt-1" checked={hold} onChange={() => setHold(!hold)} />
          <span>
            <span className="font-medium text-gray-800">Wrong-password hold</span>
            <span className="block text-xs text-gray-500">Resets their failed-password count on this account.</span>
          </span>
        </label>
        <label className={`flex items-start gap-2 ${d.keycloak.found ? '' : 'opacity-50'}`}>
          <input
            type="checkbox"
            className="mt-1"
            checked={keycloak}
            disabled={!d.keycloak.found}
            onChange={() => setKeycloak(!keycloak)}
          />
          <span>
            <span className="font-medium text-gray-800">Keycloak lock</span>
            <span className="block text-xs text-gray-500">
              {d.keycloak.locked ? 'Locked now. Unlocks the account.' : 'Not locked. Resets its failure count.'}
            </span>
          </span>
        </label>
        {blocking.map((ip) => (
          <label key={ip.client} className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" checked={ips.includes(ip.client)} onChange={() => toggleIp(ip.client)} />
            <span>
              <span className="font-medium text-gray-800">
                IP <span className="font-mono">{ip.client}</span> ({ip.banned ? 'banned' : 'over the sign-in limit'})
              </span>
              {ip.shared ? (
                <span className="block text-xs font-medium text-amber-700">
                  {ip.accounts_24h} accounts signed in from this IP today. Clearing it lets all of them in.
                </span>
              ) : (
                <span className="block text-xs text-gray-500">Clears its sign-in limit and ban.</span>
              )}
            </span>
          </label>
        ))}
      </div>
    </FormDialog>
  );
};

const UserTab: React.FC<TabProps> = ({ perms }) => {
  const [params] = useSearchParams();
  const query = params.get('q') ?? '';
  const q = useRateLimitAccountQuery(query);
  const d = q.data;
  const at = q.dataUpdatedAt;
  const [clearing, setClearing] = useState(false);
  const longest = Math.max(0, ...(d?.reasons ?? []).map((r) => r.wait_seconds));
  const permanent = (d?.reasons ?? []).some((r) => r.wait_seconds === 0);

  return (
    <div className="space-y-5">
      <Card title="Find a user">
        <AccountSearch initial={query} autoFocus={!query} />
        <p className="mt-2 text-xs text-gray-500">
          Checks everything that can stop this person signing in: too many wrong passwords, a Keycloak lock, and
          limits or bans on the network they sign in from.
        </p>
      </Card>

      {query && (
        <Frame q={q} loading="Checking this person's sign-in.">
          {d && (
            <>
              {!d.account && (
                <Notice>
                  No account matches “{d.query}”. Holds are still checked against what was typed.
                </Notice>
              )}
              <div
                className={`rounded-xl border p-5 shadow-sm ${
                  d.can_login ? 'border-green-100 bg-green-50' : 'border-red-100 bg-red-50'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    {d.can_login ? (
                      <CheckCircle2 className="mt-1 h-8 w-8 text-green-600" />
                    ) : (
                      <Lock className="mt-1 h-8 w-8 text-red-600" />
                    )}
                    <div>
                      <p className="text-sm text-gray-700">
                        {d.account?.name ?? d.query}
                        {d.account && (
                          <span className="ml-2 text-gray-500">
                            @{d.account.username}
                            {d.account.email ? ` · ${d.account.email}` : ''}
                            {d.account.phone ? ` · ${d.account.phone}` : ''}
                          </span>
                        )}
                      </p>
                      {d.can_login ? (
                        <p className="text-lg font-semibold text-green-800">Nothing is blocking sign-in</p>
                      ) : (
                        <p className="text-lg font-semibold text-red-800">
                          Can&apos;t sign in
                          {!permanent && (
                            <>
                              {' '}for another <Countdown seconds={longest} at={at} className="tabular-nums" />
                            </>
                          )}
                        </p>
                      )}
                      {d.can_login && (
                        <p className="text-xs text-gray-600">
                          If they still cannot sign in, it is most likely the password itself: see the attempts below.
                        </p>
                      )}
                      <ul className="mt-2 space-y-1">
                        {d.reasons.map((r, i) => (
                          <li key={i} className="text-sm text-gray-800">
                            • {r.text}{' '}
                            {r.wait_seconds > 0 ? (
                              <span className="text-gray-600">
                                Ends in <Countdown seconds={r.wait_seconds} at={at} className="tabular-nums" />.
                              </span>
                            ) : r.clearable ? (
                              <span className="text-gray-600">Stays until cleared.</span>
                            ) : (
                              <span className="text-gray-600">Not a rate limit: fix it on the user&apos;s account.</span>
                            )}
                            {r.shared && (
                              <span className="block pl-3 text-xs font-medium text-amber-700">
                                This IP is shared by many accounts, so clearing it lets all of them in.
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  {perms.canManage && (
                    <Button
                      variant={d.can_login ? 'outline' : 'destructive'}
                      onClick={() => setClearing(true)}
                    >
                      <Unlock className="mr-2 h-4 w-4" />
                      {d.can_login ? 'Reset counters' : 'Let them sign in'}
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid gap-5 xl:grid-cols-2">
                <Card title="Wrong-password hold" note={`${d.holds[0]?.fail_limit ?? 10} failures → ${fmtSecs(d.holds[0]?.hold_seconds)} hold`} flush>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs text-gray-500">
                      <tr>
                        <Th>Typed as</Th>
                        <Th>Wrong passwords</Th>
                        <Th>Hold</Th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {d.holds.map((h) => (
                        <tr key={h.identifier} className={h.held ? 'bg-red-50/40' : ''}>
                          <td className="px-4 py-2 text-gray-800">{h.identifier}</td>
                          <td className="px-4 py-2 tabular-nums text-gray-700">
                            {h.failures} <span className="text-gray-400">of {h.fail_limit}</span>
                          </td>
                          <td className="px-4 py-2">
                            {h.held ? (
                              <span className="text-red-700">
                                <Countdown seconds={h.hold_remaining_seconds} at={at} className="tabular-nums" /> left
                              </span>
                            ) : (
                              <span className="text-gray-400">none</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
                <Card title="Keycloak lock">
                  {!d.keycloak.available ? (
                    <p className="text-sm text-gray-500">Keycloak&apos;s lock status could not be read.</p>
                  ) : !d.keycloak.found ? (
                    <p className="text-sm text-gray-500">No Keycloak account found.</p>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-3">
                      <Field label="Status">
                        <StatusPill blocked={!!d.keycloak.locked} label={d.keycloak.locked ? 'locked' : 'not locked'} />
                      </Field>
                      <Field label="Failed attempts">{d.keycloak.failures ?? 0}</Field>
                      <Field label="Last failure">
                        {d.keycloak.last_failure_at ? fmtAgo(d.keycloak.last_failure_at) : '—'}
                      </Field>
                    </div>
                  )}
                </Card>
              </div>

              <Card title="Networks they signed in from" note="limits and bans are per IP" flush>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs text-gray-500">
                      <tr>
                        <Th>IP</Th>
                        <Th>Seen in</Th>
                        <Th>Accounts on it today</Th>
                        <Th>Sign-in</Th>
                        <Th>Ban</Th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {d.ips.length === 0 ? (
                        <EmptyRow cols={5}>
                          {d.attempts_have_ip || d.attempts.length === 0
                            ? 'No IP recorded for this person.'
                            : 'Their sign-in attempts are older than IP recording, so no IP is known yet.'}
                        </EmptyRow>
                      ) : (
                        d.ips.map((ip) => (
                          <tr key={ip.client_id} className={ip.banned || ip.login_blocked ? 'bg-red-50/40' : ''}>
                            <td className="px-4 py-2">
                              <ClientCell c={ip} />
                            </td>
                            <td className="px-4 py-2 text-gray-600">{ip.source}</td>
                            <td className="px-4 py-2">
                              <span className={ip.shared ? 'font-medium text-amber-700' : 'text-gray-700'}>
                                {ip.accounts_24h}
                                {ip.shared && ' · shared'}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              {ip.login_blocked ? (
                                <span className="text-red-700">
                                  blocked, <Countdown seconds={ip.login_retry_after_seconds} at={at} className="tabular-nums" />
                                </span>
                              ) : (
                                <span className="text-gray-400">ok</span>
                              )}
                            </td>
                            <td className="px-4 py-2">
                              {ip.banned ? (
                                <span className="text-red-700">
                                  level {ip.ban_level}, <Countdown seconds={ip.ban_remaining_seconds} at={at} className="tabular-nums" />
                                </span>
                              ) : ip.strikes ? (
                                <span className="text-amber-700">{ip.strikes} strikes</span>
                              ) : (
                                <span className="text-gray-400">none</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>

              <Card title="Recent sign-in attempts" note="last 7 days" flush>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs text-gray-500">
                      <tr>
                        <Th>When</Th>
                        <Th>Typed as</Th>
                        <Th>Result</Th>
                        <Th>IP</Th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {d.attempts.length === 0 ? (
                        <EmptyRow cols={4}>No sign-in attempts in the last 7 days.</EmptyRow>
                      ) : (
                        d.attempts.map((a, i) => (
                          <tr key={`${a.timestamp}-${i}`}>
                            <td className="whitespace-nowrap px-4 py-2 text-gray-600" title={fmtTime(a.timestamp)}>
                              {fmtAgo(a.timestamp)}
                            </td>
                            <td className="px-4 py-2 text-gray-700">{a.username}</td>
                            <td className="px-4 py-2">
                              {a.success ? (
                                <span className="text-green-700">signed in{a.kind && a.kind !== 'password' ? ` (${a.kind})` : ''}</span>
                              ) : (
                                <span className="text-red-700">{a.error ?? 'failed'}</span>
                              )}
                            </td>
                            <td className="px-4 py-2 font-mono text-xs text-gray-600">{a.ip ?? '—'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
              {clearing && <AccountClearDialog d={d} onClose={() => setClearing(false)} />}
            </>
          )}
        </Frame>
      )}
    </div>
  );
};

/* ---------- page ---------- */

const TAB_VIEW: Record<Tab, React.FC<TabProps>> = {
  overview: OverviewTab,
  user: UserTab,
  lookup: LookupTab,
  limits: LimitsTab,
  bans: BansTab,
  events: EventsTab,
  audit: AuditTab,
};

const RateLimitPage: React.FC<{ initialTab?: Tab }> = ({ initialTab = 'overview' }) => {
  useBreadcrumbs([
    { label: 'Rate limits', href: RATE_LIMIT_BASE },
    ...(initialTab === 'overview'
      ? []
      : [{ label: TAB_LABEL[initialTab] }]),
  ]);
  const perms = useRateLimitPerms();
  const navigate = useNavigate();
  const refresh = useRateLimitRefresh();
  const overview = useRateLimitOverviewQuery(perms.canView);
  const [clearing, setClearing] = useState<ClearTarget | null>(null);
  const View = TAB_VIEW[initialTab];

  if (perms.loading || !perms.canView) {
    return (
      <PageShell title="Rate limits" description="">
        {perms.loading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <div className="rounded-xl border border-gray-100 bg-white p-10 text-center shadow-sm">
            <p className="text-sm font-medium text-gray-800">You don&apos;t have access to Rate limits</p>
            <p className="mt-1 text-sm text-gray-500">Ask an administrator for the ratelimit.view permission.</p>
          </div>
        )}
      </PageShell>
    );
  }
  return (
    <PageShell
      title="Rate limits"
      description="Who the API is holding back: sign-in attempt limits (per IP) and progressive bans after repeated 429s. See how long each has left, and clear them for someone who is locked out."
      actions={
        <Button variant="outline" size="sm" onClick={() => refresh()}>
          <RefreshCw className="mr-2 h-3.5 w-3.5" />
          Refresh
        </Button>
      }
    >
      <div className="flex gap-2 overflow-x-auto border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => navigate(tabPath(t))}
            className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium ${
              initialTab === t
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>
      <View perms={perms} onClear={setClearing} />
      {clearing && (
        <ClearDialog
          target={clearing}
          storeShared={overview.data?.store_shared ?? true}
          onClose={() => setClearing(null)}
        />
      )}
    </PageShell>
  );
};

export default RateLimitPage;
